// Zustand store for User and Auth Details
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

const initialState = {
  user: null,
  authDetails: null,
  customerId: null,
  clientId: null,
  ablyConnectionStatus: null,
  cannedResponses: [],
  isDemoAccount: null,
  tfaReference: null,
  tfaQRCodeUrl: null,
  tfaTOTPSecret: null,
  tfaState: {
    totp: false,
    mobile: false,
  },
  tfaStatus: null,
  tfaDefaultMethod: null,
  validSetupSession: {
    totp: false,
    mobile: false,
  },
  copilotEnabled: null,
  workflowEnabled: null,
  personalizeOpensEnabled: null,
  igapiEnabled: null,
  salesBotEnabled: null,
  autoPilotFeatureEnabled: null,
  recordingFeatureEnabled: null,
  linkedinFeatureEnabled: null,
  emailFeatureEnabled: null,
  isInstagramStandaloneLoginEnabled: null,
  igOpensAvailable: null,
  opensFeatureEnabled: null,
  opensSessionActive: null,
  opensNeedLogin: null,
  sentAudioTranscriptionFeatureEnabled: null,
  selfServiceOpensEnabled: null,
  user_copilot_ai_selection: null,
  user_copilot_prompt: null,
  user_copilot_ai_service: null,
  notifications: [],
  isNotificationsUnread: null,
  currentOnboardingStage: null,
  nextOnboardingStage: null,
  isSubscriptionActive: null,
  connectedServicesCount: null,
  connectedServices: [],
};

// Sample user object
const useUserStore = create(
  devtools(
    (set) => ({
      ...initialState,
      setUser: (user) => set({ user }, false, 'set_user'),
      setAuthDetails: (authDetails) => set({ authDetails }),

      // Feature flags setters
      setIsDemoAccount: (isDemoAccount) => set({ isDemoAccount }),

      setCustomer: (customerId) => set({ customerId }),
      updateCustomerLinkedin: ({
        linkedin_name,
        linkedin_profile_image_url,
        linkedin_public_id,
      }) =>
        set((state) => {
          return {
            ...state,
            user: {
              ...state.user,
              customer: {
                ...state.user.customer,
                linkedin_name,
                linkedin_profile_image_url,
                linkedin_public_id,
              },
            },
          };
        }),
      setTFAStatus: (tfaStatus) => set({ tfaStatus }),
      setCopilotEnabled: (copilotEnabled) => set({ copilotEnabled }),
      setWorkflowEnabled: (workflowEnabled) => set({ workflowEnabled }),
      setSalesBotEnabled: (salesBotEnabled) => set({ salesBotEnabled }),
      setPersonalizeOpensEnabled: (personalizeOpensEnabled) =>
        set({ personalizeOpensEnabled }),
      setLinkedinFeatureEnabled: (linkedinFeatureEnabled) =>
        set({ linkedinFeatureEnabled }),
      setEmailFeatureEnabled: (emailFeatureEnabled) =>
        set({ emailFeatureEnabled }),
      setIsInstagramStandaloneLoginEnabled: (
        isInstagramStandaloneLoginEnabled
      ) => set({ isInstagramStandaloneLoginEnabled }),
      setOpensFeatureEnabled: (opensFeatureEnabled) =>
        set({ opensFeatureEnabled }),
      setIgOpensAvailable: (igOpensAvailable) => set({ igOpensAvailable }),
      setOpensSessionActive: (opensSessionActive) =>
        set({ opensSessionActive }),
      setOpensNeedLogin: (opensNeedLogin) => set({ opensNeedLogin }),
      setSentAudioTranscriptionFeatureEnabled: (
        sentAudioTranscriptionFeatureEnabled
      ) => set({ sentAudioTranscriptionFeatureEnabled }),
      setSelfServiceOpensEnabled: (selfServiceOpensEnabled) =>
        set({ selfServiceOpensEnabled }),
      setAutoPilotFeatureEnabled: (autoPilotFeatureEnabled) =>
        set({ autoPilotFeatureEnabled }),
      setRecordingFeatureEnabled: (recordingFeatureEnabled) =>
        set({ recordingFeatureEnabled }),
      setIgapiEnabled: (igapiEnabled) => set({ igapiEnabled }),
      setClientId: (clientId) => set({ clientId }),
      setUserCopilotAISelection: (user_copilot_ai_selection) =>
        set({ user_copilot_ai_selection }),
      setUserCopilotPrompt: (user_copilot_prompt) =>
        set({ user_copilot_prompt }),
      setUserCopilotAIService: (user_copilot_ai_service) =>
        set({ user_copilot_ai_service }),

      setAblyConnectionStatus: (status) =>
        set({ ablyConnectionStatus: status }),

      resetUserStore: () => set({ ...initialState }),

      setCannedResponses: (cannedResponses) => set({ cannedResponses }),
      addCannedResponse: (newCannedResponse) => {
        set((state) => {
          // Check if the new response is global
          if (newCannedResponse.global) {
            // Check if a non-global shortcut with the same shortcut already exists
            const existingLocalShortcut = state.cannedResponses.find(
              (response) =>
                response.shortcut === newCannedResponse.shortcut &&
                !response.global
            );

            // If a local shortcut exists, set override to true, otherwise false
            const responseWithOverride = {
              ...newCannedResponse,
              overridden: existingLocalShortcut ? true : false,
            };

            return {
              cannedResponses: [...state.cannedResponses, responseWithOverride],
            };
          } else {
            // For non-global responses, check if a global response with same shortcut exists
            const existingGlobalShortcut = state.cannedResponses.find(
              (response) =>
                response.shortcut === newCannedResponse.shortcut &&
                response.global
            );

            if (existingGlobalShortcut) {
              // Update the existing global response to set overridden to true
              const updatedResponses = state.cannedResponses.map((response) =>
                response._id === existingGlobalShortcut._id
                  ? { ...response, overridden: true }
                  : response
              );

              return {
                cannedResponses: [...updatedResponses, newCannedResponse],
              };
            }

            // default scenario
            return {
              cannedResponses: [...state.cannedResponses, newCannedResponse],
            };
          }
        });
      },
      updateCannedResponse: (updatedCannedResponse) => {
        set((state) => {
          // Check if the updated response is global
          if (updatedCannedResponse.global) {
            // Check if a non-global shortcut with the same shortcut already exists
            const existingLocalShortcut = state.cannedResponses.find(
              (response) =>
                response.shortcut === updatedCannedResponse.shortcut &&
                !response.global &&
                response._id !== updatedCannedResponse._id // Exclude the current response being updated
            );

            // If a local shortcut exists, set override to true, otherwise false
            const responseWithOverride = {
              ...updatedCannedResponse,
              overridden: existingLocalShortcut ? true : false,
            };

            return {
              cannedResponses: state.cannedResponses.map((cannedResponse) =>
                cannedResponse._id === updatedCannedResponse._id
                  ? responseWithOverride
                  : cannedResponse
              ),
            };
          } else {
            // For non-global responses, check if a global response with same shortcut exists
            const existingGlobalShortcut = state.cannedResponses.find(
              (response) =>
                response.shortcut === updatedCannedResponse.shortcut &&
                response.global &&
                response._id !== updatedCannedResponse._id // Exclude the current response being updated
            );

            if (existingGlobalShortcut) {
              // Update the existing global response to set overridden to true
              const updatedResponses = state.cannedResponses.map((response) => {
                if (response._id === existingGlobalShortcut._id) {
                  return { ...response, overridden: true };
                } else if (response._id === updatedCannedResponse._id) {
                  return updatedCannedResponse;
                }
                return response;
              });

              return {
                cannedResponses: updatedResponses,
              };
            }

            // If no global shortcut exists, just update the response normally
            return {
              cannedResponses: state.cannedResponses.map((cannedResponse) =>
                cannedResponse._id === updatedCannedResponse._id
                  ? updatedCannedResponse
                  : cannedResponse
              ),
            };
          }
        });
      },
      removeCannedResponse: (cannedResponseId) => {
        set((state) => {
          // Find the response being deleted
          const responseToDelete = state.cannedResponses.find(
            (response) => response._id === cannedResponseId
          );

          if (!responseToDelete) {
            return state; // Response not found, return current state
          }

          // Remove the response
          const filteredResponses = state.cannedResponses.filter(
            (cannedResponse) => cannedResponse._id !== cannedResponseId
          );

          // If the deleted response is global, just remove it
          if (responseToDelete.global) {
            return {
              cannedResponses: filteredResponses,
            };
          }

          // If the deleted response is local, check if a global response with the same shortcut exists
          const globalShortcutWithSameName = filteredResponses.find(
            (response) =>
              response.shortcut === responseToDelete.shortcut && response.global
          );

          // If a global shortcut exists, set its overridden to false
          if (globalShortcutWithSameName) {
            const updatedResponses = filteredResponses.map((response) => {
              if (response._id === globalShortcutWithSameName._id) {
                return {
                  ...response,
                  overridden: false,
                };
              }
              return response;
            });

            return {
              cannedResponses: updatedResponses,
            };
          }

          // Default case: just remove the response
          return {
            cannedResponses: filteredResponses,
          };
        });
      },
      setTFASetupData: (qrCodeUrl, totpSecret) =>
        set({ tfaQRCodeUrl: qrCodeUrl, tfaTOTPSecret: totpSecret }),
      clearTFASetupData: () =>
        set({
          tfaQRCodeUrl: null,
          tfaTOTPSecret: null,
        }),
      setTFAState: (method, value) =>
        set((state) => ({
          tfaState: {
            ...state.tfaState,
            [method]: value,
          },
        })),
      setTFADefaultMethod: (method) => set({ tfaDefaultMethod: method }),
      setTFAReference: (reference) => set({ tfaReference: reference }),
      setValidSetupSession: (method, value) =>
        set((state) => ({
          validSetupSession: {
            ...state.validSetupSession,
            [method]: value,
          },
        })),
      clearValidSetupSessions: () =>
        set(() => ({
          validSetupSession: {
            totp: false,
            mobile: false,
          },
        })),

      // Notifications
      setNotifications: (notifications) => set({ notifications }),
      updateIsNotificationsUnread: (isNotificationsUnread) =>
        set({ isNotificationsUnread }),

      // Onboarding
      setCurrentOnboardingStage: (currentOnboardingStage) =>
        set({ currentOnboardingStage }),
      setNextOnboardingStage: (nextOnboardingStage) =>
        set({ nextOnboardingStage }),
      setIsSubscriptionActive: (isSubscriptionActive) =>
        set({ isSubscriptionActive }),
      setConnectedServicesCount: (connectedServicesCount) =>
        set({ connectedServicesCount }),
      setConnectedServices: (connectedServices) => set({ connectedServices }),
      updateReadAllNotifications: () =>
        set((state) => ({
          notifications: state.notifications.map((notification) => ({
            ...notification,
            is_read: true,
          })),
        })),
      updateReadNotification: (notificationId) =>
        set((state) => ({
          notifications: state.notifications.map((notification) =>
            notification._id === notificationId
              ? { ...notification, is_read: true }
              : notification
          ),
        })),
    }),
    { name: 'userStore' }
  )
);

export default useUserStore;
